/* eslint-disable react-hooks/exhaustive-deps */
import { MutableRefObject, RefObject, useCallback } from "react";
import { t } from "app/utils";
import { UnsplashImageResponse } from "chq";
import { useSignUpload } from "app/hooks";
import Editor from "components/editor/Editor";
import { VideoDimension } from "components/stories/types";
import useUploadImages from "./useUploadImages";
import useUploadVideo from "./useUploadVideo";
import useAddTags from "./useAddTags";
import usePostStory from "./usePostStory";
import { isMediaVideoType } from "../utils";
import { Media, PostStoryProviderProps, PostStoryState, SetState } from "../types";

type UseHandleSubmit = {
  videoDurationRef: MutableRefObject<number | undefined>;
  editorRef: RefObject<Editor | undefined>;
  editorCopyRef: RefObject<Editor | undefined>;
  aiStoryBodyRef: RefObject<Editor | undefined>;
  selectedOptionsRef: MutableRefObject<any[] | undefined>;
  videoDimensionRef: MutableRefObject<VideoDimension | undefined>;
  prompts: PostStoryProviderProps["prompts"];
  state: PostStoryState;
  setState: SetState;
  onAnotherModalOpen: PostStoryProviderProps["onAnotherModalOpen"];
  onNotification: PostStoryProviderProps["onNotification"];
  onPromptChange: PostStoryProviderProps["onPromptChange"];
  onPosted: PostStoryProviderProps["onPosted"];
  onClose: () => void;
  closeModal: () => void;
  restartState: () => void;
};

const useHandleSubmit = ({
  videoDurationRef,
  videoDimensionRef,
  editorRef,
  editorCopyRef,
  aiStoryBodyRef,
  selectedOptionsRef,
  prompts,
  state: {
    userSelectedImage,
    progress,
    selectedPrompt,
    selectedQuestion,
    selectedImages,
    selectedMedia,
    selectedChannel,
    thumbnail,
    mappersValueIds,
    mediaData,
    webVideoRecorded,
    currentStory,
    questions,
    suggestions,
    selectedVideos
  },
  setState,
  onAnotherModalOpen,
  onNotification,
  onPromptChange,
  onPosted,
  onClose,
  closeModal,
  restartState
}: UseHandleSubmit) => {
  const signUpload = useSignUpload();
  const uploadImages = useUploadImages({ setState });
  const uploadVideo = useUploadVideo({ progress, setState });
  const addTags = useAddTags({ tags: selectedOptionsRef.current });
  const waitForS3 = usePostStory({
    state: { selectedMedia, selectedPrompt, webVideoRecorded, currentStory },
    setState,
    videoDimensionRef,
    prompts,
    onAnotherModalOpen,
    onNotification,
    onPromptChange,
    onPosted,
    onClose,
    closeModal,
    restartState
  });

  return useCallback(
    async ({ forceSubmit, newPublicUser } = {}) => {
      if (!userSelectedImage) {
        switch (selectedPrompt?.allowedTypes) {
          case "just_videos":
            setState({ error: t("Please select a video to share") });
            break;
          case "just_images":
            setState({ error: t("Please select an image to share") });
            break;
          default:
            setState({ error: t("Please select an image or video to share") });
            break;
        }
        return;
      }

      const questionId = selectedPrompt?.id || selectedQuestion;
      if (!questionId) {
        setState({ error: t("Please select a prompt in order to post your story") });
        return;
      }

      if (!editorCopyRef.current && !editorRef.current && !aiStoryBodyRef.current) {
        return;
      }

      setState({ postingStory: true });
      if (progress === 0 && !forceSubmit) setState({ progress: 1 });

      const post = async () => {
        let mediaType;
        let videoLength = 0;
        let avatarUrl;
        let visuals: Media[] = [];
        let mediaToUpload = newPublicUser?.avatarUrl ? 1 : 0;

        if (selectedImages.length > 0) {
          mediaType = "image";
          visuals = (await uploadImages(
            selectedImages as (Media & UnsplashImageResponse)[]
          )) as Media[];

          if (newPublicUser?.avatarUrl) {
            avatarUrl = await signUpload(
              newPublicUser.avatarUrl,
              (mediaProgress: number) => {
                setState({ progress: 90 + mediaProgress / 10 });
              }
            );
          }
        } else if (
          isMediaVideoType(selectedMedia as Blob)
          && (selectedMedia as Blob).size
        ) {
          // This case is a video
          mediaType = "video";
          videoLength = videoDurationRef.current || 0;

          const visualsThumb = thumbnail ? await signUpload(thumbnail) : "";
          if (selectedVideos) {
            videoLength = 0;
            mediaToUpload += selectedVideos.length;

            const getVideoDuration = (videoFile: File): Promise<number> => new Promise(resolve => {
              const videoElement = document.createElement("video");
              videoElement.src = URL.createObjectURL(videoFile);
              videoElement.preload = "metadata";
              videoElement.onloadedmetadata = () => {
                const { duration } = videoElement;
                URL.revokeObjectURL(videoElement.src);
                resolve(duration);
              };
            });

            const mediaVisuals = await Promise.all(
              selectedVideos.map(async selectedVid => {
                const mediaUrl = await uploadVideo(selectedVid, mediaToUpload);
                const currentVidDuration = await getVideoDuration(selectedVid);
                videoLength += currentVidDuration;
                return {
                  mediaUrl,
                  thumbUrl: visualsThumb,
                  fullUrl: "",
                  smallUrl: ""
                } as Media;
              })
            );

            visuals = mediaVisuals;
          } else {
            mediaToUpload += 1;
            const mediaUrl = await uploadVideo(selectedMedia, mediaToUpload);
            visuals = [
            {
              mediaUrl,
              thumbUrl: visualsThumb,
              fullUrl: "",
              smallUrl: ""
            } as Media
            ];
          }

          if (newPublicUser?.avatarUrl) {
            avatarUrl = await signUpload(
              newPublicUser.avatarUrl,
              (mediaProgress: number) => {
                const progressRatio = progress === 1 ? 0.75 : 0.9;
                setState({
                  progress:
                    (progressRatio * (100 + mediaProgress)) / mediaToUpload
                });
              }
            );
          }
        }

        const body = aiStoryBodyRef.current || (await editorRef.current?.serialize())
          || (await editorCopyRef.current?.serialize())
          || "";
        const bodyWithTags = addTags(body as string);

        await waitForS3(
          2000,
          questionId,
          bodyWithTags,
          selectedChannel,
          visuals,
          mediaType,
          videoLength,
          mediaData,
          newPublicUser
            ? { ...newPublicUser, avatarUrl }
            : undefined,
          questions,
          suggestions,
          mappersValueIds
        );
      };

      setState({ error: undefined });
      post();
    },
    [
      addTags,
      editorCopyRef,
      editorRef,
      aiStoryBodyRef,
      mediaData,
      progress,
      selectedChannel,
      selectedImages,
      selectedMedia,
      selectedPrompt,
      selectedQuestion,
      questions,
      suggestions,
      setState,
      signUpload,
      thumbnail,
      uploadImages,
      uploadVideo,
      userSelectedImage,
      videoDurationRef,
      waitForS3
    ]
  );
};

export default useHandleSubmit;
